import { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, FloatingLabel, Alert, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import LocationAutocomplete from 'components/common/LocationAutocomplete';
import { Coordinates } from 'features/location/googleLocation';
import {
  createShift,
  getShiftById,
  updateShift
} from 'features/shift/shiftApi';
import { useSearchParams } from 'react-router-dom';

const PharmacistCreateShiftPage = () => {
  const [formData, setFormData] = useState({
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    address: '',
    latitude: 0,
    longitude: 0,
    pay_rate: '',
    required_locums: '',
    locum_role: 'pharmacist',
    summary: ''
  });

  const [searchParams] = useSearchParams();
  const shiftId = searchParams.get('id');

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!shiftId) return;

    const fetchShift = async () => {
      try {
        const data = await getShiftById(shiftId);
        console.log('Fetched shift data:', data);

        setFormData({
          date: data.date.split('T')[0],
          start_time: data.start_time.slice(0, 5),
          end_time: data.end_time.slice(0, 5),
          location: data.location,
          address: data.address,
          latitude: data.latitude,
          longitude: data.longitude,
          pay_rate: data.pay_rate,
          required_locums: data.required_locums,
          locum_role: data.locum_role,
          summary: data.summary || ''
        });
      } catch (err) {
        console.error(err);
      }
    };

    fetchShift();
  }, [shiftId]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]:
        name === 'pay_rate' || name === 'required_locums'
          ? Number(value)
          : value
    });
  };

  const handleLocationChange = useCallback((location: string) => {
    setFormData(prev => ({
      ...prev,
      location
    }));
  }, []);

  const handleCoordinatesChange = useCallback((coords: Coordinates) => {
    setFormData(prev => ({
      ...prev,
      latitude: coords.lat,
      longitude: coords.lng
    }));
  }, []);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (formData.start_time >= formData.end_time) {
      return setError('End time must be after start time');
    }

    try {
      setLoading(true);

      let res;

      if (shiftId) {
        res = await updateShift(shiftId, formData);
      } else {
        res = await createShift(formData);
      }

      if (res.success) {
        setSuccess(
          shiftId ? 'Shift updated successfully' : 'Shift created successfully'
        );

        if (!shiftId) {
          setFormData({
            date: '',
            start_time: '',
            end_time: '',
            location: '',
            address: '',
            latitude: 0,
            longitude: 0,
            pay_rate: '',
            required_locums: '',
            locum_role: 'pharmacist',
            summary: ''
          });
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create shift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <h3 className="mb-4">Create Shift</h3>

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={6}>
                <FloatingLabel label="Date">
                  <Form.Control
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                  />
                </FloatingLabel>
              </Col>

              <Col md={3}>
                <FloatingLabel label="Start Time">
                  <Form.Control
                    type="time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleChange}
                    required
                  />
                </FloatingLabel>
              </Col>

              <Col md={3}>
                <FloatingLabel label="End Time">
                  <Form.Control
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleChange}
                    required
                  />
                </FloatingLabel>
              </Col>

              <Col md={6}>
                <LocationAutocomplete
                  value={formData.location}
                  onChange={handleLocationChange}
                  onCoordinatesChange={handleCoordinatesChange}
                  label="Location"
                  placeholder="Search pharmacy location"
                  required
                />
              </Col>
              <Col md={6}>
                <FloatingLabel label="Full Address">
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Street, building, landmark..."
                    required
                  />
                </FloatingLabel>
              </Col>

              {/* <Col md={6}>
                <FloatingLabel label="Latitude (optional)">
                  <Form.Control
                    type="text"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                  />
                </FloatingLabel>
              </Col>

              <Col md={6}>
                <FloatingLabel label="Longitude (optional)">
                  <Form.Control
                    type="text"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                  />
                </FloatingLabel>
              </Col> */}

              <Col md={6}>
                <FloatingLabel label="Pay Rate">
                  <Form.Control
                    type="number"
                    name="pay_rate"
                    value={formData.pay_rate}
                    onChange={handleChange}
                    required
                  />
                </FloatingLabel>
              </Col>

              <Col md={6}>
                <FloatingLabel label="Required Locums">
                  <Form.Control
                    type="number"
                    name="required_locums"
                    value={formData.required_locums}
                    onChange={handleChange}
                    required
                  />
                </FloatingLabel>
              </Col>
              <Col md={6}>
                <FloatingLabel label="Required Locum Role">
                  <Form.Select
                    name="locum_role"
                    value={formData.locum_role}
                    onChange={handleChange}
                    required
                  >
                    <option value="pharmacist">Pharmacist</option>
                    <option value="technician">Technician</option>
                  </Form.Select>
                </FloatingLabel>
              </Col>

              <Col md={12}>
                <FloatingLabel label="Summary (optional)">
                  <Form.Control
                    as="textarea"
                    name="summary"
                    value={formData.summary}
                    onChange={handleChange}
                    style={{ height: '100px' }}
                  />
                </FloatingLabel>
              </Col>

              <Col md={12}>
                <Button
                  type="submit"
                  variant="phoenix-primary"
                  loading={loading}
                  startIcon={<FontAwesomeIcon icon={faSave} />}
                >
                  {shiftId ? 'Update Shift' : 'Create Shift'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default PharmacistCreateShiftPage;
